import { useState } from 'react'
import { EGButton } from '../../../../MainComponents/Button/EGButton/EGButton';
import { darken, useTheme, Stack, CircularProgress, alpha } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useIsHovering } from '../../../../../hooks/useIsHovering';
import Icon from '../../../../Icon/Icon';

// *  [light, primary, secondary, error, warning, info, success]

export const InvestigationButtonOld = ({
  variant,
  outlined = false,
  icon,
  iconNameHover,
  iconSize = 17,
  label,
  size,
  onClick,
  sx,
  iconColor,
  iconColorHover,
  ...rest
}) => {

  const theme = useTheme();

  const haveLabel = (() => {
    if (!label) return {
      padding: 0,
      minWidth: 30,
      maxWidth: 30,
    };
    return {};
  })();

  const hoverFinal = (() => {
    if (variant === 'success') return {
      background: darken(theme.palette.green.main, 0.2),
      borderColor: darken(theme.palette.green.main, 0.2),
      color: theme.palette.white.main
    };
    if (variant === 'danger') return {
      background: darken(theme.palette.red.main, 0.2),
      borderColor: darken(theme.palette.red.main, 0.2),
      color: theme.palette.white.main
    };
    if (variant === 'main') return {
      background: darken(theme.palette.primary.main, 0.2),
      border: `1px solid ${darken(theme.palette.primary.main, 0.2)}`,
      color: theme.palette.white.main
    };
  })();

  const labelColorFinal = (() => {
    if (outlined) {
      if (variant === 'success') return "green";
      if (variant === 'danger') return "red";
      if (variant === 'main') return "primary";
    } else {
      if (variant === 'success') return "white";
      if (variant === 'danger') return "white";
      if (variant === 'main') return "white";
    }
    return undefined;
  })();

  const hoverLabelColorFinal = (() => {
    if (variant === 'success') return "white";
    if (variant === 'danger') return "white";
    if (variant === 'main') return "white";
  
    return undefined;
  })();

  const iconSizeFinal = (() => {
    if (size === 'medium') return 24;
    return 17;
  })();

  return (
    <EGButton
      label={label}
      size={size ?? "thin"}
      iconName={icon ?? "outline_edit_form"}
      iconNameHover={iconNameHover}
      color={variant === 'main' ? "primary" : variant === "primary" ? undefined : variant}
      labelColor={labelColorFinal}
      labelColorHover={hoverLabelColorFinal}
      iconColor={labelColorFinal}
      iconColorHover={hoverLabelColorFinal}
      onClick={typeof onClick === "function" ? onClick : () => {}}
      iconSize={iconSizeFinal}
      {...(outlined && { variant: "outlined" })}
      sx={{ 
        whiteSpace: "nowrap",
        // overflow: "hidden",
        // textOverflow: "hidden",
        transition: "all 0.1s ease-in-out",
        // ...backgroundFinal,
        ...haveLabel,
        ...sx,
        "&:hover": {
          ...hoverFinal
        },
      }}
      {...rest}
    />
  )
}

export const InvestigationButton = ({
  outlined = false,
  variant = "light",
  icon,
  iconHover,
  iconPosition = "start",
  iconSize = 17,
  label,
  fullWidth = false,
  href,
  size = "medium",
  onClick,
  sx = {},
  iconColor,
  iconColorHover,
  loading = false,
  loadingPosition = "center",
  loadingIndicator,
  inverted = false,
  disabled = false,
  bg,
  width,
  wordBreak = "normal",
  // loadingColor = "primary",
  ...rest
}) => {

  const theme = useTheme();

  const [isHovering, handleMouseEnter, handleMouseLeave] = useIsHovering();

  // Button background color (only if variant === "light")
  const lightBackgroundGradient = !outlined
      ? `linear-gradient(to bottom, ${theme.palette.common.white}, ${theme.palette.blueGray200.main} 140%)`
      : "transparent";
  // Button background hover color (only if variant === "light")
  const lightHoverGradient = !outlined
      ? `linear-gradient(to bottom, ${theme.palette.common.white}, ${theme.palette.blueGray500.main} 140%)`
      : `linear-gradient(to bottom, ${theme.palette.common.white}, ${theme.palette.blueGray200.main} 140%)`;
  const lightDisabledBackground = `linear-gradient(to top, ${theme.palette.paleGrey3.main}, ${theme.palette.white.main})`;

  const haveLabel = (() => {
    if (!label && icon) return {
      padding: 0,
      maxHeight: 30,
      minHeight: 30,
      minWidth: 30,
      maxWidth: 30,
    };
    if (label && icon) {
      return {
        py: 0,
        width: fullWidth ? "100%" : width ?? 180,
        textWrap: "nowrap",
      };
    }
    if (label && !icon) {
      return {
        py: 0,
        width: fullWidth ? "100%" : width ?? 160,
        textWrap: "nowrap",
      };
    }
    return {};
  })();

  const bgFinal = (() => {
    if (disabled) {
      return {
        background: `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${theme.palette.paleGrey3.main} 100%)`,
        border: `1px solid ${theme.palette.blueGray200.main}`,
        color: theme.palette.blueGray200.main
      };
    }
    if (outlined) {
      if (variant === 'success') return {
        background: theme.palette.white.main,
        border: `1px solid ${theme.palette.green.main}`,
        color: theme.palette.green.main
      };
      if (variant === 'danger') return {
        background: theme.palette.white.main,
        border: `1px solid ${theme.palette.red.main}`,
        color: theme.palette.red.main
      };
      if (variant === 'main') return {
        background: theme.palette.white.main,
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main
      };
      if (variant === 'custom') return {
        background: theme.palette.white.main,
        border: `1px solid ${bg}`,
        color: bg
      };
    } else {
      if (variant === 'success') return {
        background: theme.palette.green.main,
        border: `2px solid ${theme.palette.green.main}`,
        color: theme.palette.white.main
      };
      if (variant === 'danger') return {
        background: theme.palette.red.main,
        border: `2px solid ${theme.palette.red.main}`,
        color: theme.palette.white.main
      };
      if (variant === 'main') return {
        background: theme.palette.primary.main,
        border: `2px solid ${theme.palette.primary.main}`,
        color: theme.palette.white.main
      };
      if (variant === 'custom') return {
        background: bg,
        border: `2px solid ${bg}`,
        color: theme.palette.white.main
      };
    }
    return {
      background: lightBackgroundGradient,
      border: `1px solid ${theme.palette.silver.main}`,
      color: theme.palette.blueGray.main
    };
  })();

  const bgHoverFinal = (() => {
    if (disabled) {
      return {};
    }
    if (outlined) {
      if (variant === 'success') return {
        background: darken(theme.palette.green.main, 0.2),
        border: `1px solid ${darken(theme.palette.green.main, 0.2)}`,
        color: theme.palette.white.main
      };
      if (variant === 'danger') return {
        background: darken(theme.palette.red.main, 0.2),
        border: `1px solid ${darken(theme.palette.red.main, 0.2)}`,
        color: theme.palette.white.main
      };
      if (variant === 'main') return {
        background: darken(theme.palette.primary.main, 0.2),
        border: `1px solid ${darken(theme.palette.primary.main, 0.2)}`,
        color: theme.palette.white.main
      };
      if (variant === 'custom') return {
        background: darken(bg, 0.2),
        border: `1px solid ${darken(bg, 0.2)}`,
        color: theme.palette.white.main
      };
    } else if (inverted) {
      if (variant === 'success') return {
        background: theme.palette.white.main,
        border: `2px solid ${theme.palette.green.main}`,
        color: theme.palette.green.main
      };
      if (variant === 'danger') return {
        background: theme.palette.white.main,
        border: `2px solid ${theme.palette.red.main}`,
        color: theme.palette.red.main
      };
      if (variant === 'main') return {
        background: theme.palette.white.main,
        border: `2px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main
      };
      if (variant === 'custom') return {
        background: theme.palette.white.main,
        border: `2px solid ${bg}`,
        color: bg
      };
    } else {
      if (variant === 'success') return {
        background: darken(theme.palette.green.main, 0.2),
        border: `2px solid ${darken(theme.palette.green.main, 0.2)}`,
        color: theme.palette.white.main
      };
      if (variant === 'danger') return {
        background: darken(theme.palette.red.main, 0.2),
        border: `2px solid ${darken(theme.palette.red.main, 0.2)}`,
        color: theme.palette.white.main
      };
      if (variant === 'main') return {
        background: darken(theme.palette.primary.main, 0.2),
        border: `2px solid ${darken(theme.palette.primary.main, 0.2)}`,
        color: theme.palette.white.main
      };
      if (variant === 'custom') return {
        background: darken(bg, 0.2),
        border: `2px solid ${darken(bg, 0.2)}`,
        color: theme.palette.white.main
      };
    }
      return {
      background: lightHoverGradient,
      border: `1px solid ${theme.palette.silver.main}`,
      color: theme.palette.blueGray.main
    };
  })();

  const labelColorFinal = (() => {
    if (disabled) return theme.palette.blueGray200.main;
    if (outlined) {
      if (variant === 'success') return "green";
      if (variant === 'danger') return "red";
      if (variant === 'main') return "primary";
    } else if (inverted) {
      if (variant === 'success') return "white";
      if (variant === 'danger') return "white";
      if (variant === 'main') return "white";
    } else {
      if (variant === 'success') return "white";
      if (variant === 'danger') return "white";
      if (variant === 'main') return "white";
    }
    return theme.palette.blueGray.main;
  })();

  const hoverLabelColorFinal = (() => {
    if (disabled) return theme.palette.blueGray200.main;
    if (outlined) {
      if (variant === 'success') return "white";
      if (variant === 'danger') return "white";
      if (variant === 'main') return "white";
    } else if (inverted) {
      if (variant === 'success') return darken(theme.palette.green.main, 0.2);
      if (variant === 'danger') return darken(theme.palette.red.main, 0.2);
      if (variant === 'main') return darken(theme.palette.primary.main, 0.2);
    } else {
      if (variant === 'success') return "white";
      if (variant === 'danger') return "white";
      if (variant === 'main') return "white";
    }
    return theme.palette.blueGray.main;
  })();

  // Initial button size is defined
  function sizeStyles() {
    switch (size) {
      case "thin":
        return { 
          maxHeight: "30px", 
          minWidth: icon && !label ? 30 : width ?? 180,
          maxWidth: icon && !label ? 30 : width ?? 180,
          fontSize: "12.5px", 
          paddingTop: 0, 
          paddingBottom: 0,
          // width: width ?? 180,
        };
      case "small":
        return { minHeight: "34px", fontSize: "11.5px" };
      case "medium":
        return { minHeight: "38px", fontSize: "12.5px" };
      case "large":
        return { minHeight: "2.5rem", fontSize: "15.5px" };
      default:
        return {};
    }
  };

  // Selected icon rendering
  function renderIcon() {
    if (!icon) return null;
    return (
      <Icon
        name={iconHover && isHovering ? iconHover : icon}
        size={iconSize}
        color={
          loading
            ? theme.palette.blueGray200.main
            : isHovering 
              ? iconColorHover ? iconColorHover
                : iconColor ? iconColor
                : labelColorFinal
              : iconColor ? iconColor
                : labelColorFinal
        }
        className="mx-1"
      />
    );
  };
  const iconFinal = renderIcon();

  // Conditional rendering of the load indicator
  function renderLoadingIndicator() {
    if (!loading) return null;

    // If it contains a loadingIndicator and Icon
    if (loadingIndicator && icon) {
      return (
        <Stack
          spacing={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection={iconPosition === "start" ? "row" : "row-reverse"}
        >
          {/* Display circle instead of icon */}
          <CircularProgress
            color={theme.palette.primary.main}
            size={iconSize + 2}
          />
          {/*  Show loadingIndicator text instead of the label */}
          <span>{loadingIndicator}</span>
        </Stack>
      );
    }

    // If it contains a loadingIndicator but no icon
    if (loadingIndicator) {
      return loadingIndicator;
    }

    // If it does not contain a loadingIndicator, display only the circle
    return <CircularProgress size={iconSize + 1} />;
  };

  return (
    <LoadingButton
      aria-busy={loading}
      disabled={disabled}
      disableElevation={true}
      fullWidth={fullWidth}
      href={href}
      startIcon={iconPosition === "start" && label ? iconFinal : null}
      endIcon={iconPosition === "end" && label ? iconFinal : null}
      variant={variant}
      size={size}
      onClick={onClick}
      loading={loading}
      loadingIndicator={renderLoadingIndicator()}
      loadingPosition={loadingPosition}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        whiteSpace: "wrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        py: 0.6,
        transition: "all 100ms ease-in-out",
        ...bgFinal,
        "&:hover": {
          ...bgHoverFinal,
          "& .MuiButton-icon": {
            color: hoverLabelColorFinal,
          }
        },
        "&.Mui-disabled": {
          background: lightDisabledBackground,
          color: theme.palette.blueGray200.main,
          border: `1px solid ${theme.palette.blueGray200.main}`,
        },
        "& span.MuiLoadingButton-label": {
          wordBreak,
        },
        ...sizeStyles(),
        ...haveLabel,
        ...sx,
      }}
    >
      {/* If there is no label, the icon is centered */}
      {label || (!label && iconFinal)}
    </LoadingButton>
  )
}